import React from 'react';
import PropTypes from 'prop-types';
import Icon from '../../../icon';
import { CalculationContext } from '../../context/CalculationContext';

// BACS classes ranked from lowest (D) to highest (A)
const BACS_CLASSES = ['D', 'C', 'B', 'A'];

const getComparisonModifier = (minimumRequirement, selected) => {
    const minimumRank = BACS_CLASSES.indexOf(minimumRequirement);
    const selectedRank = BACS_CLASSES.indexOf(selected);

    if (minimumRank === -1 || selectedRank === -1 || minimumRank === selectedRank) {
        return '';
    }

    return selectedRank > minimumRank ? ' energy-calculator-bacs-classifications-block__selected--better' : ' energy-calculator-bacs-classifications-block__selected--worse';
};

// Group name props from the initial page response, matched against "Group" in the calculate response
const GROUP_NAME_PROPS = [
    { match: 'domestichotwater', prop: 'domesticHotWaterGroupName' },
    { match: 'heating', prop: 'heatingControlGroupName' },
    { match: 'cooling', prop: 'coolingGroupName' },
    { match: 'ventilation', prop: 'ventilationGroupName' },
    { match: 'lighting', prop: 'lightingGroupName' },
    { match: 'blinds', prop: 'blindsControlGroupName' },
    { match: 'technicalhome', prop: 'technicalHomeGroupName' }
];

const getGroupHeading = (group, props) => {
    const normalizedGroup = (group || '').toLowerCase().replace(/[^a-z]/g, '');
    const groupNameProp = GROUP_NAME_PROPS.find(({ match }) => normalizedGroup.includes(match));

    return (groupNameProp && props[groupNameProp.prop]) || group;
};

// Groups the flat BACS rows by "Group", numbered by the chapter prefix ("1.2" -> "1")
const getBacsTable = (bacsRows, props) => (
    bacsRows.reduce((chapters, row) => {
        let chapter = chapters.find((item) => item.group === row.Group);
        if (!chapter) {
            chapter = {
                group: row.Group,
                number: (row.Chapter || '').split('.')[0],
                heading: getGroupHeading(row.Group, props),
                rows: []
            };
            chapters.push(chapter);
        }
        chapter.rows.push({
            chapter: row.Chapter,
            control: row.Name,
            minimumRequirement: row.MinimumRequirement,
            selected: row.Selected,
            swegonFunction: row.SwegonFunction
        });

        return chapters;
    }, [])
);

const BacsClassificationsBlock = (props) => {
    const { enableBACS, bacsHeading, bacsPreamble, bacsCTA, translations = {} } = props;
    const calculationContext = React.useContext(CalculationContext);
    const [expanded, setExpanded] = React.useState(false);

    if (!enableBACS || !calculationContext.calculation) {
        return null;
    }

    const bacsTable = getBacsTable(calculationContext.calculation.BACS || [], props);

    const functionalities = [
        { label: translations.airHandlingUnitWBuiltinControlsLabel, selected: calculationContext.airHandlingUnitWithBuiltInControls },
        { label: translations.dcvAirOptimisationLabel, selected: calculationContext.dCVAirOptimisation },
        { label: translations.waterTemperatureOptimisationLabel, selected: calculationContext.aHUWatertempOptimisation },
        { label: translations.waterTemperatureOptimisationRoomLabel, selected: calculationContext.dCVWatertempOptimisationRoom },
        { label: translations.waterflowOptimisationLabel, selected: calculationContext.waterflowOptimisation },
        { label: translations.hydronicOptimisationLabel, selected: calculationContext.hydronicOptimisation },
        { label: translations.analyticsAndVisualisationLabel, selected: calculationContext.analyticsandVisualisation }
    ];
    const sortedFunctionalities = [
        ...functionalities.filter((functionality) => functionality.selected),
        ...functionalities.filter((functionality) => !functionality.selected)
    ];

    return (
        <div className='energy-calculator-bacs-classifications-block'>
            <div className='energy-calculator-bacs-classifications-block__intro'>
                {bacsHeading && <h3>{bacsHeading}</h3>}
                {bacsPreamble && <p className='preamble'>{bacsPreamble}</p>}
                {bacsCTA && bacsCTA.href && (
                    <a
                        href={bacsCTA.href}
                        target={bacsCTA.target || undefined}
                        title={bacsCTA.title || undefined}
                        className='button button--arrow energy-calculator-bacs-classifications-block__link'
                    >
                        <span className='button__label'>{bacsCTA.text}</span>
                        <Icon icon='chevron-right-old' additionalClasses='button__icon' />
                    </a>
                )}
            </div>

            <div
                id='bacs-comparison-table'
                className={`energy-calculator-bacs-classifications-block__body${expanded || bacsTable.length === 0 ? '' : ' energy-calculator-bacs-classifications-block__body--collapsed'}`}
            >
                <div className='energy-calculator-bacs-classifications-block__functionality'>
                    {translations.bacsSelectedFunctionsTitle && (
                        <h4 className='energy-calculator-bacs-classifications-block__functionality-heading'>
                            {translations.bacsSelectedFunctionsTitle}
                        </h4>
                    )}
                    <ul className='energy-calculator-bacs-classifications-block__functionality-list'>
                        {sortedFunctionalities.map((functionality) => (
                            <li
                                key={functionality.label}
                                className={`energy-calculator-bacs-classifications-block__functionality-item${functionality.selected ? ' energy-calculator-bacs-classifications-block__functionality-item--selected' : ''}`}
                            >
                                <Icon
                                    icon={functionality.selected ? 'check-thin' : 'close-thin'}
                                    additionalClasses='energy-calculator-bacs-classifications-block__functionality-icon'
                                />
                                <span>{functionality.label}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {bacsTable.length > 0 && (
                    <div className='energy-calculator-bacs-classifications-block__table-wrapper'>
                        <table className='energy-calculator-bacs-classifications-block__table'>
                            <thead>
                                <tr>
                                    <th scope='col' className='energy-calculator-bacs-classifications-block__chapter'>{translations.bacsTableChapterTitle}</th>
                                    <th scope='col'>{translations.bacsTableControlTitle}</th>
                                    <th scope='col' className='energy-calculator-bacs-classifications-block__class'>{translations.bacsTableMinimumRequirementTitle}</th>
                                    <th scope='col' className='energy-calculator-bacs-classifications-block__class'>{translations.bacsTableSelectedTitle}</th>
                                    <th scope='col' className='energy-calculator-bacs-classifications-block__function'>{translations.bacsTableSwegonFunctionTitle}</th>
                                </tr>
                            </thead>
                            {bacsTable.map((chapter) => (
                                <tbody key={chapter.number}>
                                    <tr className='energy-calculator-bacs-classifications-block__chapter-row'>
                                        <th scope='rowgroup'>{chapter.number}</th>
                                        <th scope='rowgroup' colSpan={4}>{chapter.heading}</th>
                                    </tr>
                                    {chapter.rows.map((row) => (
                                        <tr key={row.chapter} className='energy-calculator-bacs-classifications-block__row'>
                                            <td>{row.chapter}</td>
                                            <td>{row.control}</td>
                                            <td className='energy-calculator-bacs-classifications-block__class'>{row.minimumRequirement}</td>
                                            <td className={`energy-calculator-bacs-classifications-block__class energy-calculator-bacs-classifications-block__selected${getComparisonModifier(row.minimumRequirement, row.selected)}`}>
                                                {row.selected}
                                            </td>
                                            <td>{row.swegonFunction}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            ))}
                        </table>
                    </div>
                )}
            </div>

            {bacsTable.length > 0 && (
                <div className='energy-calculator-bacs-classifications-block__footer'>
                    <button
                        type='button'
                        className='button button--ghost energy-calculator-bacs-classifications-block__toggle'
                        aria-expanded={expanded}
                        aria-controls='bacs-comparison-table'
                        onClick={() => setExpanded(!expanded)}
                    >
                        <span className='button__label'>
                            {expanded ? translations.bacsTableHideComparisonText : translations.bacsTableShowComparisonText}
                        </span>
                    </button>
                </div>
            )}
        </div>
    );
};

BacsClassificationsBlock.propTypes = {
    enableBACS: PropTypes.bool,
    bacsHeading: PropTypes.string,
    bacsPreamble: PropTypes.string,
    bacsCTA: PropTypes.shape({
        href: PropTypes.string,
        target: PropTypes.string,
        title: PropTypes.string,
        text: PropTypes.string
    }),
    heatingControlGroupName: PropTypes.string,
    domesticHotWaterGroupName: PropTypes.string,
    coolingGroupName: PropTypes.string,
    ventilationGroupName: PropTypes.string,
    lightingGroupName: PropTypes.string,
    blindsControlGroupName: PropTypes.string,
    technicalHomeGroupName: PropTypes.string,
    translations: PropTypes.object
};

export default BacsClassificationsBlock;
